import React, { useState, useEffect } from "react";
import Nav from "./Navigation.js";
import { getTokenInternal } from "./useToken";
import { useNavigate } from "react-router-dom";

export default function ManageAcquisitionMethods() {
  const [token, setToken] = useState(null);
  const [methods, setMethods] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [message, setMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      const t = await getTokenInternal();
      if (!t || !["owner", "management"].includes(t.account.role)) {
        navigate("/");
        return;
      }
      setToken(t);
    };
    init();
  }, []);

  const load = async () => {
    const res = await fetch(`${process.env.REACT_APP_API_HOST}/api/acquisition-methods`);
    if (res.ok) setMethods(await res.json());
  };

  useEffect(() => {
    if (token) load();
  }, [token]);

  const flash = (msg) => {
    setMessage(msg);
    setTimeout(() => setMessage(""), 4000);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const res = await fetch(`${process.env.REACT_APP_API_HOST}/api/acquisition-methods/create`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token.access_token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ name, description: description || null }),
    });
    if (res.ok) {
      setName("");
      setDescription("");
      flash("Created!");
      load();
    } else {
      flash("Could not create — name may already exist.");
    }
  };

  const startEdit = (method) => {
    setEditingId(method.id);
    setEditName(method.name);
    setEditDescription(method.description || "");
  };

  const handleUpdate = async (id) => {
    const res = await fetch(`${process.env.REACT_APP_API_HOST}/api/acquisition-methods/${id}`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token.access_token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ name: editName, description: editDescription || null }),
    });
    if (res.ok) {
      setEditingId(null);
      flash("Updated!");
      load();
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this acquisition method?")) return;
    await fetch(`${process.env.REACT_APP_API_HOST}/api/acquisition-methods/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token.access_token}` },
    });
    flash("Deleted.");
    load();
  };

  if (!token) return null;

  return (
    <div className="text-white">
      <Nav />
      <div className="container mx-auto p-4 max-w-2xl">
        <h1 className="text-2xl font-bold mb-2">Acquisition Methods</h1>
        <p className="text-gray-300 mb-6">
          How items are earned in-game (e.g. In-game Store, XP Milestone, VR System, Pig Mask Run).
          Used to sub-group items under the "Other" event in the tracker.
        </p>

        {message && <p className="text-green-400 mb-4">{message}</p>}

        <form onSubmit={handleCreate} className="mb-8 flex flex-wrap gap-2 items-end">
          <div>
            <label className="block text-sm mb-1">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="In-game Store"
              required
              className="p-1 border rounded text-black w-48"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Description (optional)</label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Items purchased with in-game tickets"
              className="p-1 border rounded text-black w-72"
            />
          </div>
          <button type="submit" className="p-1 border rounded bg-blue-600 hover:bg-blue-700">
            Add
          </button>
        </form>

        {methods.length === 0 ? (
          <p className="text-gray-400">No acquisition methods yet.</p>
        ) : (
          <div className="space-y-2">
            {methods.map((m) => (
              <div key={m.id} className="border border-gray-500 rounded p-3">
                {editingId === m.id ? (
                  <div className="flex flex-wrap gap-2 items-end">
                    <input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="p-1 border rounded text-black w-48"
                    />
                    <input
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      className="p-1 border rounded text-black w-72"
                      placeholder="Description"
                    />
                    <button
                      onClick={() => handleUpdate(m.id)}
                      className="p-1 border rounded bg-green-600 hover:bg-green-700 text-sm"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="p-1 border rounded text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="font-bold">{m.name}</span>
                      {m.description && (
                        <p className="text-sm text-gray-300 mt-0.5">{m.description}</p>
                      )}
                    </div>
                    <div className="flex gap-2 ml-4 shrink-0">
                      <button
                        onClick={() => startEdit(m)}
                        className="p-1 border rounded text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(m.id)}
                        className="p-1 border rounded text-sm text-red-400"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
